import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  onAuthStateChanged,
  User as FirebaseUser
} from '@angular/fire/auth';

import { UserService } from './user.service';
import { User } from '../models/user';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loggedIn$ = new BehaviorSubject<boolean>(false);
  private currentUser: FirebaseUser | null = null;

  // Key for storing redirect URL in sessionStorage
  private readonly RETURN_URL_KEY = 'returnUrl';

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private userService: UserService,
    private auth: Auth
  ) {
    // Subscribe to Firebase auth state
    onAuthStateChanged(this.auth, (user) => {
      this.currentUser = user;
      this.loggedIn$.next(!!user);
    });
  }

  // Observable for components to subscribe to
  get isLoggedIn() {
    return this.loggedIn$.asObservable();
  }

  /**
   * Store the URL user was trying to access before auth redirect
   */
  setReturnUrl(url: string): void {
    sessionStorage.setItem(this.RETURN_URL_KEY, url);
  }

  /**
   * Get and clear the stored return URL
   */
  private getAndClearReturnUrl(): string {
    const url = sessionStorage.getItem(this.RETURN_URL_KEY);
    sessionStorage.removeItem(this.RETURN_URL_KEY);
    return url || '/models';
  }

  // Register a user with auto-profile creation
  async register(email: string, password: string, displayName: string): Promise<void> {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Send verification email
      try {
        await sendEmailVerification(userCredential.user);
        this.snackBar.open('Verification email sent! Please check your inbox.', 'Close', {
          duration: 7000,
        });
      } catch (err) {
        this.snackBar.open('Failed to send verification email.', 'Close', {
          duration: 7000,
        });
        console.error('Verification email error', err);
      }

      // Create Firestore user profile with auto-profile creation
      const userData: Partial<User> = {
        user_id: userCredential.user?.uid,
        email: userCredential.user?.email,
        is_verified: userCredential.user?.emailVerified
      };

      await new Promise<void>((resolve, reject) => {
        this.userService.initializeUserProfile(userData, displayName).subscribe({
          next: ({ user, isModel }) => {
            const accountType = isModel ? 'model' : 'client';
            this.snackBar.open(`Welcome ${displayName}! Your ${accountType} account is ready.`, 'Close', { duration: 7000 });
            
            // Clear registration data after successful registration
            localStorage.removeItem('referralCode');
            localStorage.removeItem('accountType');
            
            // Redirect to original route or default
            const returnUrl = this.getAndClearReturnUrl();
            this.router.navigate([returnUrl]);
            resolve();
          },
          error: (err) => {
            this.snackBar.open('Profile initialization failed', 'Close', {
              duration: 7000,
            });
            console.error('Profile initialization error', err);
            reject(err);
          }
        });
      });

    } catch (error: any) {
      this.handleRegistrationError(error);
      throw error; // let caller know
    }
  }

  // Login user
  async login(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
      // Redirect to original route or default
      const returnUrl = this.getAndClearReturnUrl();
      this.router.navigate([returnUrl]);
      this.snackBar.open('Login successful!', 'Close', { duration: 7000 });
    } catch (error: any) {
      this.handleLoginError(error);
      throw error;
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await this.auth.signOut();
      this.router.navigate(['/login']);
      this.snackBar.open('Logged out successfully!', 'Close', { duration: 7000 });
    } catch (error) {
      this.snackBar.open('Logout failed', 'Close', { duration: 7000 });
      console.error('Logout error', error);
    }
  }

  private handleRegistrationError(error: any) {
    let errorMessage = 'Registration error';

    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'This email is already in use.';
    } else {
      errorMessage = `Registration error: ${error.message}`;
    }

    this.snackBar.open(errorMessage, 'Close', { duration: 7000 });
  }

  private handleLoginError(error: any) {
    let errorMessage = 'Login error';

    if (error.code === 'auth/invalid-credential') {
      errorMessage = 'Invalid credentials.';
    } else {
      errorMessage = `Login error: ${error.message}`;
    }

    this.snackBar.open(errorMessage, 'Close', { duration: 7000 });
  }

  // Returns UID of logged in user
  getCurrentUid(): string | null {
    return this.currentUser?.uid || null;
  }
}
